import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'components';
import {
  AlertCircle,
  ChevronDown,
  Circle,
  CircleCheck,
  Info,
  RefreshCw,
  XIcon,
} from 'lucide-react';
import {
  type ExportStatus,
  ExportStatusValue,
  type Patient,
  ResultTemplateKey,
} from 'types/result';
import { type BillingScheinStatus, ScheinStatusValue } from 'types/billingScheinStatus';
import { formatDateTime } from 'utils';
import { BillingScheinBanner } from 'widgets/billingSchein';
import { BillingScheinDialog, type BillingScheinDialogMode } from './BillingScheinDialog';
import {
  IcdCompleteBannerBox,
  IcdCompleteBannerCloseIcon,
  IcdCompleteBannerDismissButton,
  IcdCompleteBannerIcon,
  IcdCompleteBannerMessage,
  IcdCompleteBannerText,
  IcdProcessingBannerBox,
  IcdProcessingBannerIcon,
  IcdProcessingBannerSubtext,
  IcdProcessingBannerText,
  IcdProcessingBannerTitle,
} from './ExportStatusWidget.styles';
import { IcdTransferGuardDialog, type IcdTransferGuardMode } from './IcdTransferGuardDialog';
import { ResultBottomNavBar } from './ResultBottomNavbar';
import { SuggestionsErrorBanner } from './SuggestionsErrorBanner';

// A code that arrived this recently is treated as "just completed": the physician may
// not have had time to notice it before hitting transfer, so we ask them to confirm/review.
const ICD_RECENTLY_COMPLETED_MS = 1_500;

const SECTION_LABEL_KEYS: Record<string, string> = {
  [ResultTemplateKey.Anamnesis]: 'result.section.anamnesis',
  [ResultTemplateKey.Findings]: 'result.section.findings',
  [ResultTemplateKey.Diagnosis]: 'result.section.diagnosis',
  [ResultTemplateKey.Therapy]: 'result.section.therapy',
  [ResultTemplateKey.Procedure]: 'result.section.procedure',
  [ResultTemplateKey.Comment]: 'result.section.comment',
  [ResultTemplateKey.DiagnosedIcd]: 'result.section.diagnosis',
  [ResultTemplateKey.EbmCodes]: 'result.ebmProposals.title',
  [ResultTemplateKey.VitalParameters]: 'result.section.vitalParameters',
  [ResultTemplateKey.SelfAnamnesis]: 'result.selfAnamnesis',
  [ResultTemplateKey.Immunization]: 'result.immunization.blockTitle',
};

interface SectionResult {
  key: string;
  label: string;
  success: boolean;
}

function useSectionResults(exportStatus?: ExportStatus | null) {
  const { t } = useTranslation();

  return useMemo(() => {
    const entries = exportStatus?.result;
    if (!entries?.length) return undefined;

    const order: string[] = [];
    const aggregated = new Map<string, { total: number; succeeded: number }>();

    entries.forEach((entry) => {
      const type = entry.type;
      if (!aggregated.has(type)) {
        aggregated.set(type, { total: 0, succeeded: 0 });
        order.push(type);
      }
      const agg = aggregated.get(type)!;
      agg.total += 1;
      if (entry.successfulTransmitted) agg.succeeded += 1;
    });

    return order.map((type) => {
      const { total, succeeded } = aggregated.get(type)!;
      return {
        key: type,
        label: SECTION_LABEL_KEYS[type] ? t(SECTION_LABEL_KEYS[type]) : type,
        success: succeeded === total,
      };
    });
  }, [exportStatus?.result, t]);
}

function ExportFailureBanner({ text, onDismiss }: { text: string; onDismiss: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between gap-4 bg-destructive px-4 py-3" role="alert">
      <div className="flex items-center gap-3 min-w-0">
        <AlertCircle className="size-5 shrink-0 text-white" aria-hidden />
        <p className="text-sm text-white">{text}</p>
      </div>
      <button
        onClick={onDismiss}
        className="flex shrink-0 size-8 items-center justify-center rounded-full border border-white text-white hover:bg-white/10"
        aria-label={t('general.dismiss')}
      >
        <XIcon className="size-4" aria-hidden />
      </button>
    </div>
  );
}

function IcdProcessingBanner({ slow }: { slow: boolean }) {
  const { t } = useTranslation();
  return (
    <IcdProcessingBannerBox>
      <IcdProcessingBannerIcon />
      <IcdProcessingBannerText>
        <IcdProcessingBannerTitle>
          {t(slow ? 'result.icdProcessing.slowTitle' : 'result.icdProcessing.banner')}
        </IcdProcessingBannerTitle>
        {slow && (
          <IcdProcessingBannerSubtext>
            {t('result.icdProcessing.slowHint')}
          </IcdProcessingBannerSubtext>
        )}
      </IcdProcessingBannerText>
    </IcdProcessingBannerBox>
  );
}

function IcdCompleteBanner({ onDismiss }: { onDismiss: () => void }) {
  const { t } = useTranslation();
  return (
    <IcdCompleteBannerBox>
      <IcdCompleteBannerText>
        <IcdCompleteBannerIcon />
        <IcdCompleteBannerMessage>{t('result.icdProcessing.complete')}</IcdCompleteBannerMessage>
      </IcdCompleteBannerText>
      <IcdCompleteBannerDismissButton onClick={onDismiss} aria-label={t('general.dismiss')}>
        <IcdCompleteBannerCloseIcon />
      </IcdCompleteBannerDismissButton>
    </IcdCompleteBannerBox>
  );
}

function ProcessingIndicator() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <Spinner size="xxs" animate />
      <span>{t('result.transferStatus.processing')}</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="h-4 w-4 cursor-default" />
        </TooltipTrigger>
        <TooltipContent>{t('result.transferStatus.processingTooltip')}</TooltipContent>
      </Tooltip>
    </div>
  );
}

function SuccessIndicator({ dateDone }: { dateDone?: string | null }) {
  const { t } = useTranslation();
  const formattedDate = dateDone ? formatDateTime(dateDone, { separator: ' · ' }) : '';
  return (
    <div className="flex items-center gap-2 text-sm text-teal-600">
      <CircleCheck className="h-4 w-4" />
      <span>{t('result.transferStatus.success')}</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="h-4 w-4 cursor-default text-muted-foreground" />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs whitespace-pre-line">
          {t('result.transferStatus.successTooltip', { date: formattedDate })}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

function FailureControls({
  isTransferring,
  onRetry,
  onShowDetails,
}: {
  isTransferring: boolean;
  onRetry: () => void;
  onShowDetails: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex rounded-md shadow-sm">
      <Button
        onClick={onRetry}
        disabled={isTransferring}
        className="rounded-r-none border-r border-primary-foreground/20"
      >
        <RefreshCw className="h-4 w-4 mr-1.5" />
        {t('result.transferStatus.retry')}
      </Button>
      <Button
        onClick={onShowDetails}
        disabled={isTransferring}
        className="rounded-l-none px-2"
        aria-label={t('result.transferStatus.dialog.title')}
      >
        <ChevronDown className="h-4 w-4" />
      </Button>
    </div>
  );
}

function FailureDetailsDialog({
  open,
  onOpenChange,
  sectionResults,
  isTransferring,
  onRetry,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sectionResults?: SectionResult[];
  isTransferring: boolean;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  const successCount = sectionResults?.filter((s) => s.success).length ?? 0;
  const totalCount = sectionResults?.length ?? 0;
  const hasPartialDetails = totalCount > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('result.transferStatus.dialog.title')}</DialogTitle>
          <DialogDescription className="text-red-600 font-medium">
            {hasPartialDetails
              ? t('result.transferStatus.dialog.partialFailure', {
                  success: successCount,
                  total: totalCount,
                })
              : t('result.transferStatus.failed')}
          </DialogDescription>
        </DialogHeader>

        <p className="text-sm text-foreground">{t('result.transferStatus.dialog.body')}</p>

        {hasPartialDetails && (
          <div className="rounded-md border border-border p-4 grid grid-cols-2 gap-x-4 gap-y-2">
            {sectionResults!.map(({ key, label, success }) => (
              <div key={key} className="flex items-center gap-1.5 text-sm">
                {success ? (
                  <CircleCheck className="h-4 w-4 text-teal-600 shrink-0" />
                ) : (
                  <Circle className="h-4 w-4 text-muted-foreground shrink-0" />
                )}
                <span className={success ? 'text-foreground' : 'text-muted-foreground'}>
                  {label}
                </span>
              </div>
            ))}
          </div>
        )}

        <p className="text-sm text-muted-foreground">{t('result.transferStatus.dialog.footer')}</p>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t('general.dismiss')}
          </Button>
          <Button
            onClick={() => {
              onOpenChange(false);
              onRetry();
            }}
            disabled={isTransferring}
          >
            <RefreshCw className="h-4 w-4 mr-1.5" />
            {t('result.transferStatus.retry')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface ExportStatusWidgetProps {
  exportStatus?: ExportStatus | null;
  isTransferring?: boolean;
  hasEdits?: boolean;
  transferLabel?: string;
  patient?: Patient | null;
  scheinStatus?: BillingScheinStatus | null;
  isScheinChecking?: boolean;
  icdPending?: boolean;
  icdProcessingSlow?: boolean;
  icdCodesReceivedAt?: number | null;
  icdJustCompleted?: boolean;
  suggestionsFailed?: boolean;
  isRetryingSuggestions?: boolean;
  onDismissIcdComplete?: () => void;
  onScheinRecheck?: () => void;
  onRetrySuggestions?: () => void;
  onTransfer: () => void;
  onRetry: () => void;
  onDismiss: () => void;
}

function dialogModeFor(scheinStatus?: BillingScheinStatus | null): BillingScheinDialogMode | null {
  if (!scheinStatus) return null;
  if (scheinStatus.status === ScheinStatusValue.NoSchein) return 'no_schein';
  if (scheinStatus.status === ScheinStatusValue.WrongType) return 'wrong_type';
  return null;
}

export function ExportStatusWidget({
  exportStatus,
  isTransferring = false,
  hasEdits = false,
  transferLabel,
  patient,
  scheinStatus,
  isScheinChecking = false,
  icdPending = false,
  icdProcessingSlow = false,
  icdCodesReceivedAt = null,
  icdJustCompleted = false,
  suggestionsFailed = false,
  isRetryingSuggestions = false,
  onDismissIcdComplete,
  onScheinRecheck,
  onRetrySuggestions,
  onTransfer,
  onRetry,
  onDismiss,
}: ExportStatusWidgetProps) {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [scheinDialogMode, setScheinDialogMode] = useState<BillingScheinDialogMode | null>(null);
  const [icdGuardMode, setIcdGuardMode] = useState<IcdTransferGuardMode | null>(null);
  const [pendingAction, setPendingAction] = useState<'transfer' | 'retry' | null>(null);

  const status = exportStatus?.status;
  const isFailed = status === ExportStatusValue.Failure || status === ExportStatusValue.Error;
  const isProcessing =
    status === ExportStatusValue.Pending ||
    status === ExportStatusValue.Progress ||
    status === ExportStatusValue.Retry;
  const isSuccess = status === ExportStatusValue.Success && !hasEdits;
  const showExportButton =
    !status ||
    status === ExportStatusValue.NoStatus ||
    (status === ExportStatusValue.Success && hasEdits);

  const sectionResults = useSectionResults(exportStatus);
  const successCount = useMemo(
    () => sectionResults?.filter((s) => s.success).length ?? 0,
    [sectionResults]
  );
  const totalCount = sectionResults?.length ?? 0;

  useEffect(() => {
    setBannerDismissed(false);
  }, [exportStatus?.id]);

  // Keep the open dialog in sync with the latest schein status (e.g. after a recheck).
  useEffect(() => {
    if (!scheinDialogMode) return;
    const next = dialogModeFor(scheinStatus);
    if (next !== scheinDialogMode) setScheinDialogMode(next);
  }, [scheinStatus, scheinDialogMode]);

  const failureBanner =
    isFailed && !bannerDismissed ? (
      <ExportFailureBanner
        text={
          totalCount > 0
            ? t('result.transferStatus.failureBanner', {
                success: successCount,
                total: totalCount,
              })
            : t('result.transferStatus.failureBannerGeneric')
        }
        onDismiss={() => setBannerDismissed(true)}
      />
    ) : undefined;

  const scheinBanner = onScheinRecheck ? (
    <BillingScheinBanner
      scheinStatus={scheinStatus}
      onRecheck={onScheinRecheck}
      isRechecking={isScheinChecking}
    />
  ) : undefined;

  const icdBanner = icdPending ? <IcdProcessingBanner slow={icdProcessingSlow} /> : undefined;

  const icdCompleteBanner =
    icdJustCompleted && onDismissIcdComplete ? (
      <IcdCompleteBanner onDismiss={onDismissIcdComplete} />
    ) : undefined;

  const suggestionsBanner =
    suggestionsFailed && onRetrySuggestions ? (
      <SuggestionsErrorBanner onRetry={onRetrySuggestions} isRetrying={isRetryingSuggestions} />
    ) : undefined;

  const banner = (
    <>
      {icdCompleteBanner}
      {icdBanner}
      {failureBanner}
      {scheinBanner}
      {suggestionsBanner}
    </>
  );

  const runAction = (action: 'transfer' | 'retry') => {
    if (action === 'retry') {
      onRetry();
    } else {
      onTransfer();
    }
  };

  const runPastIcdGuard = (action: 'transfer' | 'retry') => {
    const mode = dialogModeFor(scheinStatus);
    if (mode) {
      setPendingAction(action);
      setScheinDialogMode(mode);
      return;
    }
    runAction(action);
  };

  const handleGuardedClick = (action: 'transfer' | 'retry') => () => {
    if (action === 'transfer') {
      if (icdPending) {
        setPendingAction(action);
        setIcdGuardMode('pending');
        return;
      }
      if (
        icdCodesReceivedAt !== null &&
        Date.now() - icdCodesReceivedAt < ICD_RECENTLY_COMPLETED_MS
      ) {
        setPendingAction(action);
        setIcdGuardMode('recent');
        return;
      }
    }
    runPastIcdGuard(action);
  };

  const handleIcdGuardConfirm = () => {
    setIcdGuardMode(null);
    runPastIcdGuard(pendingAction ?? 'transfer');
  };

  const handleIcdGuardDismiss = () => {
    setIcdGuardMode(null);
    setPendingAction(null);
  };

  const handleConfirmTransfer = () => {
    const action = pendingAction ?? 'transfer';
    setScheinDialogMode(null);
    setPendingAction(null);
    runAction(action);
  };

  const handleCloseDialog = () => {
    setScheinDialogMode(null);
    setPendingAction(null);
  };

  const defaultLabel = transferLabel ?? t('result.export');

  const controls = !patient ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0}>
          <Button disabled>{defaultLabel}</Button>
        </span>
      </TooltipTrigger>
      <TooltipContent>{t('result.exportError.noPatient')}</TooltipContent>
    </Tooltip>
  ) : (
    <>
      {isProcessing && <ProcessingIndicator />}
      {isSuccess && <SuccessIndicator dateDone={exportStatus?.dateDone} />}
      {isFailed && (
        <FailureControls
          isTransferring={isTransferring}
          onRetry={handleGuardedClick('retry')}
          onShowDetails={() => setDialogOpen(true)}
        />
      )}
      {showExportButton && (
        <Button onClick={handleGuardedClick('transfer')} disabled={isTransferring}>
          {defaultLabel}
        </Button>
      )}
    </>
  );

  return (
    <>
      <ResultBottomNavBar banner={banner} rightContent={controls} onDismiss={onDismiss} />
      <FailureDetailsDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        sectionResults={sectionResults}
        isTransferring={isTransferring}
        onRetry={handleGuardedClick('retry')}
      />
      {icdGuardMode && (
        <IcdTransferGuardDialog
          mode={icdGuardMode}
          onDismiss={handleIcdGuardDismiss}
          onConfirm={handleIcdGuardConfirm}
        />
      )}
      {scheinDialogMode && scheinStatus && (
        <BillingScheinDialog
          mode={scheinDialogMode}
          scheinStatus={scheinStatus}
          isRechecking={isScheinChecking}
          onClose={handleCloseDialog}
          onRecheck={() => onScheinRecheck?.()}
          onConfirm={handleConfirmTransfer}
        />
      )}
    </>
  );
}
